import React, { useContext, useMemo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { stripesConnect, useOkapiKy, useStripes, CalloutContext } from '@folio/stripes-core';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import SaveNewReportForm from './SaveNewReportForm';
import useQueryString from '../../../../hooks/useQueryString';
import displayMethodsConst from '../../../../constants/DisplayMethods';

function SaveNewReportManager({
  showQueryBuilderPane,
  setShowQueryBuilderPane,
  showSavePane,
  setShowSavePane,
  handleClose,
  resources,
  mutator,
  query,
  builderType,
  builderState,
  saveMode = 'new',
  savedReportId,
  savedReportName,
  savedReportDesc,
}) {
  const [queryString] = useQueryString(query);

  // Extract dimensions from query for use in visualization selectors
  const queryDimensions = useMemo(() => {
    const q = typeof query === 'string'
      ? (() => { try { return JSON.parse(query); } catch { return {}; } })()
      : (query || {});
    return q.dimensions || [];
  }, [query]);
  const intl = useIntl();
  const okapiKy = useOkapiKy();
  const stripes = useStripes();
  const queryClient = useQueryClient();
  const callout = useContext(CalloutContext);
  const canCloseRef = useRef(true);
  const currentUserId = stripes.user?.user?.id;
  const tenantCurrencyCode = stripes?.currency || 'USD';
  const CURRENCY_SYMBOLS = {
    USD: '$', EUR: '\u20ac', GBP: '\u00a3', SAR: '\u0631.\u0633', AED: '\u062f.\u0625',
    KWD: '\u062f.\u0643', QAR: '\u0631.\u0642', BHD: '\u062f.\u0628', OMR: '\u0631.\u0639', JOD: '\u062f.\u0623',
    EGP: '\u062c.\u0645', MAD: '\u062f.\u0645', TND: '\u062f.\u062a', JPY: '\u00a5', CNY: '\u00a5',
    INR: '\u20b9', TRY: '\u20ba', CAD: 'CA$', AUD: 'A$',
  };
  const defaultCurrencySymbol = CURRENCY_SYMBOLS[tenantCurrencyCode] || tenantCurrencyCode;
  const isUpdateMode = saveMode === 'update' && savedReportId;
  const [fullReportData, setFullReportData] = useState(null);
  useEffect(() => {
    if (isUpdateMode && savedReportId) {
      okapiKy.get(`reports/${savedReportId}`).json()
        .then(data => setFullReportData(data))
        .catch(err => console.error('Failed to fetch report:', err));
    } else {
      setFullReportData(null);
    }
  }, [isUpdateMode, savedReportId]);

  const initialValues = useMemo(() => {
    // In update mode: pre-fill with full report data from API
    if (isUpdateMode && fullReportData) {
      // Convert displayMethods from strings to {value, label} objects for MultiSelection
      const rawMethods = fullReportData.visualization?.displayMethods || [];
      const convertedMethods = rawMethods.map(m => {
        if (typeof m === 'object') return m;
        const found = displayMethodsConst.find(dm => dm.value === m || dm.value.toLowerCase() === m.toLowerCase());
        return found
          ? { value: found.value, label: intl.formatMessage(found.translationKey) }
          : { value: m, label: m };
      });

      return {
        reportName: fullReportData.name || [{ locale: 'ar', value: '' }, { locale: 'en-US', value: '' }],
        reportDesc: fullReportData.desc || [{ locale: 'ar', value: '' }, { locale: 'en-US', value: '' }],
        reportStatus: fullReportData.status || 'published',
        visibility: fullReportData.permissions?.visibility || 'public',
        reportType: fullReportData.reportType || '',
        categories: fullReportData.categoryId ? [fullReportData.categoryId] : [],
        visualization: {
          ...fullReportData.visualization,
          displayMethods: convertedMethods,
          defaultDisplayMethod: fullReportData.visualization?.defaultDisplayMethod || '',
          tableOptions: fullReportData.visualization?.tableOptions || { pagination: true, pageSize: 50, sortable: true, filterable: true, exportable: true },
          chartOptions: {
            ...(fullReportData.visualization?.chartOptions || {}),
            colorsInput: (fullReportData.visualization?.chartOptions?.colors || []).join(', '),
          },
          groupByDimension:  fullReportData.visualization?.groupByDimension  || '',
          pivotRowDimension: fullReportData.visualization?.pivotRowDimension || '',
          pivotColDimension: fullReportData.visualization?.pivotColDimension || '',
        },
        parameters: fullReportData.parameters || [],
        caching: fullReportData.caching || { enabled: true, ttl: 3600, strategy: 'normal' },
        performance: fullReportData.performance || { complexity: 'medium', dataVolume: 'medium', maxExecutionTime: 60000 },
        owner: fullReportData.permissions?.owner || '',
        viewUsers: fullReportData.permissions?.viewers || [],
        editUsers: fullReportData.permissions?.editors || [],
        executeUsers: fullReportData.permissions?.executors || [],
      };
    }
    // New report mode
    return {
      reportName: [{ locale: 'ar', value: '' }, { locale: 'en-US', value: '' }],
      reportDesc: [{ locale: 'ar', value: '' }, { locale: 'en-US', value: '' }],
      reportStatus: 'draft',
      visibility: 'public',
      reportType: '',
      categories: [],
      visualization: { displayMethods: [], defaultDisplayMethod: '', tableOptions: { pagination: true, pageSize: 50, sortable: true, filterable: true, exportable: true, showTotals: false, showSubtotals: false, frozenColumns: 0 }, chartOptions: { animations: true, responsive: true }, formatting: { numberFormat: '0,0.00', dateFormat: 'DD/MM/YYYY', currencyFormat: { symbol: defaultCurrencySymbol } } },
      caching: { enabled: true, ttl: 3600, strategy: 'normal' },
      performance: { complexity: 'medium', dataVolume: 'medium', maxExecutionTime: 60000 }
    };
  }, [isUpdateMode, fullReportData, intl]);

  const onFormSubmit = (values) => {
    // reportName and reportDesc are already in array format from FieldArray
    // Format: [{ locale: 'en', value: '...' }, { locale: 'ar', value: '...' }]
    const nameArray = Array.isArray(values.reportName) && values.reportName.length > 0
      ? values.reportName.filter(item => item.value && item.value.trim() !== '')
      : [];

    const descArray = Array.isArray(values.reportDesc) && values.reportDesc.length > 0
      ? values.reportDesc.filter(item => item.value && item.value.trim() !== '')
      : [];

    // Validate required fields
    if (!nameArray.length || !descArray.length || !values.reportType) {
      console.warn('Required fields missing: name, desc, or reportType');
      return;
    }

    // Validate owner is required for private/restricted
    if ((values.visibility === 'private' || values.visibility === 'restricted') && !values.owner) {
      canCloseRef.current = false;
      callout?.sendCallout({
        type: 'error',
        message: intl.formatMessage({
          id: 'ui-reports.permissions.ownerRequired',
          defaultMessage: 'Owner is required for private and restricted reports.',
        }),
      });
      return;
    }
    canCloseRef.current = true;

    const defaultDisplayMethod = values.visualization?.defaultDisplayMethod || null;

    // displayMethods must be List<String> on the backend
    const displayMethodsForBackend = (values.visualization?.displayMethods || []).map(method =>
      typeof method === 'object' ? method.value : method
    );

    // Parse colors from comma-separated input string to array
    const colorsInput = values.visualization?.chartOptions?.colorsInput || '';
    const colorsArray = colorsInput
      ? colorsInput.split(',').map(c => c.trim()).filter(c => c.startsWith('#') && c.length >= 4)
      : (values.visualization?.chartOptions?.colors || []);

    const chartOptions = values.visualization?.chartOptions
      ? { ...values.visualization.chartOptions, colors: colorsArray }
      : null;
    if (chartOptions) delete chartOptions.colorsInput;

    // queryString: wrap in object with sql key for backend @JsonAnySetter compatibility
    const queryStringObj = queryString
      ? { sql: queryString }
      : { sql: JSON.stringify(typeof query === 'string' ? JSON.parse(query) : query) };

    // queryMetadata: full Cube.js query object
    const queryMetadataObj = typeof query === 'string'
      ? (() => { try { return JSON.parse(query); } catch { return {}; } })()
      : (query || {});

    const backendPayload = {
      name: nameArray,
      desc: descArray,
      status: values.reportStatus || 'draft',
      reportType: values.reportType,
      categoryId: values.categories?.[0] || null,
      builderType: builderType || 'advanced',
      builderState: builderState
        ? (typeof builderState === 'string'
            ? (() => { try { return JSON.parse(builderState); } catch { return {}; } })()
            : builderState)
        : {},
      queryMetadata: queryMetadataObj,
      queryString: queryStringObj,
      permissions: {
        visibility:  values.visibility || 'public',
        // Auto-assign currentUser as owner for private/restricted if no owner selected
        owner: values.owner || (
          !isUpdateMode && (values.visibility === 'private' || values.visibility === 'restricted')
            ? (currentUserId || '')
            : ''
        ),
        // User-based permissions — matches what AuthorizedUsers writes to form
        viewers:   values.viewUsers    || [],
        editors:   values.editUsers    || [],
        executors: values.executeUsers || [],
        // Role-based permissions
        viewRoles:    values.viewRoles    || [],
        editRoles:    values.editRoles    || [],
        executeRoles: values.executeRoles || [],
      },
      visualization: {
        displayMethods: displayMethodsForBackend,
        defaultDisplayMethod,
        chartOptions: chartOptions || {},
        tableOptions: values.visualization?.tableOptions || {},
        formatting: values.visualization?.formatting || { numberFormat: '0,0.00', dateFormat: 'DD/MM/YYYY', currencyFormat: { symbol: defaultCurrencySymbol } },
        groupByDimension:   values.visualization?.groupByDimension   || undefined,
        pivotRowDimension:  values.visualization?.pivotRowDimension  || undefined,
        pivotColDimension:  values.visualization?.pivotColDimension  || undefined,
      },
      caching: values.caching || {},
      performance: values.performance || {}
    };

    if (isUpdateMode) {
      okapiKy.put(`reports/${savedReportId}`, {
        json: { ...backendPayload, id: savedReportId }
      }).then(() => {
        queryClient.invalidateQueries(['reports']);
        callout?.sendCallout({
          type: 'success',
          message: intl.formatMessage({
            id: 'ui-reports.saveReport.updateSuccess',
            defaultMessage: 'Report updated successfully.',
          }),
        });
      }).catch(err => console.error('Failed to update report:', err));
    } else {
      mutator.reports.POST(backendPayload);
    }
  };

  return (
    <>
      <SaveNewReportForm
        onSubmit={onFormSubmit}
        initialValues={initialValues}
        canCloseRef={canCloseRef}
        showQueryBuilderPane={showQueryBuilderPane}
        setShowQueryBuilderPane={setShowQueryBuilderPane}
        showSavePane={showSavePane}
        setShowSavePane={setShowSavePane}
        handleClose={handleClose}
        categories={resources.categories?.records || []}
        saveMode={saveMode}
        queryDimensions={queryDimensions}
      />
    </>
  );
}

SaveNewReportManager.propTypes = {
  showQueryBuilderPane: PropTypes.bool.isRequired,
  setShowQueryBuilderPane: PropTypes.func.isRequired,
  showSavePane: PropTypes.bool.isRequired,
  setShowSavePane: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  query: PropTypes.object.isRequired,
  builderType: PropTypes.string,
  builderState: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  resources: PropTypes.shape({
    reports: PropTypes.shape({
      records: PropTypes.arrayOf(PropTypes.object),
    }),
    reportId: PropTypes.shape({
      replace: PropTypes.func.isRequired,
    }),
  }),
  mutator: PropTypes.shape({
    reports: PropTypes.shape({
      reset: PropTypes.func.isRequired,
      POST: PropTypes.func.isRequired,
      PUT: PropTypes.func.isRequired,
    }),
    reportId: PropTypes.shape({
      replace: PropTypes.func.isRequired,
    }),
  }),
};

SaveNewReportManager.manifest = Object.freeze({
  reportId: '',
  reports: {
    type: 'okapi',
    records: 'reports',
    path: 'reports?limit=10000',
    POST: {
      path: 'reports',
    },
    PUT: {
      path: 'reports',
    },
    DELETE: {
      path: 'reports',
    },
  },
  categories: {
    type: 'okapi',
    records: 'categories',
    path: 'categories?limit=1000',
  },
  dashboards: {
    type: 'okapi',
    records: 'dashboards',
    path: 'dashboards?limit=1000',
  },
});

export default stripesConnect(SaveNewReportManager);
